// Last line of defence for the whole app. A render error anywhere used to unmount
// the React root and leave a blank page with no way back except a reload, which
// is the worst thing that can happen in front of an audience. Catch it, say what
// broke, and offer the two recoveries that actually work.

import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Seal IDE crashed while rendering:", error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="crash">
        <h1 className="crash-title">Something went wrong</h1>
        <p className="crash-text">
          The editor hit an unexpected error while rendering. Your files are saved in this browser,
          so reloading should bring everything back.
        </p>
        <pre className="crash-detail">{error.message}</pre>
        <div className="crash-actions">
          <button type="button" className="crash-btn" onClick={() => window.location.reload()}>
            Reload
          </button>
          <button
            type="button"
            className="crash-btn crash-btn--ghost"
            onClick={() => this.setState({ error: null })}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
}
